<template>
  <el-form
    ref="shipmentForm"
    :model="form"
    label-width="180px"
    class="shipment-form"
  >
    <el-card shadow="never" class="mb-3">
      <div slot="header"><strong>Informations générales</strong></div>

      <el-form-item label="Nom de l'expédition">
        <el-input v-model="form.name" />
      </el-form-item>

      <el-form-item label="Type d'emballage">
        <el-select v-model="form.packaging_type" placeholder="Sélectionner">
          <el-option
            v-for="pack in packagingTypes"
            :key="pack"
            :label="$t('ELIXIO_CONNECT.' + pack)"
            :value="pack"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Unité de poids">
        <el-select v-model="form.packaging_properties.weight.unit" placeholder="Sélectionner">
          <el-option
            v-for="unit in weightUnits"
            :key="unit"
            :label="$t('ELIXIO_CONNECT.' + unit)"
            :value="unit"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Type de paiement">
        <el-select v-model="form.payment_type" placeholder="Sélectionner">
          <el-option
            v-for="ptype in paymentTypes"
            :key="ptype"
            :label="$t('ELIXIO_CONNECT.' + ptype)"
            :value="ptype"
          />
        </el-select>
      </el-form-item>
    </el-card>

    <el-card shadow="never" class="mb-3">
      <div slot="header"><strong>Expéditeur</strong></div>

      <el-form-item label="Nom de l'entreprise">
        <el-input v-model="form.sender.company_name" />
      </el-form-item>
      <el-form-item label="Nom du contact">
        <el-input v-model="form.sender.contact_name" />
      </el-form-item>
      <el-form-item label="Courriel">
        <el-input v-model="form.sender.email_address" />
      </el-form-item>
      <el-form-item label="Téléphone">
        <el-input v-model="form.sender.address.phone_number" />
      </el-form-item>
      <el-form-item label="Adresse">
        <el-input v-model="form.sender.address.street_address" />
      </el-form-item>
      <el-form-item label="Ville">
        <el-input v-model="form.sender.address.city" />
      </el-form-item>
      <el-form-item label="Province">
        <el-input v-model="form.sender.address.region" />
      </el-form-item>
      <el-form-item label="Code postal">
        <el-input v-model="form.sender.address.postal_code" />
      </el-form-item>
    </el-card>

    <el-card shadow="never" class="mb-3">
      <div slot="header"><strong>Destinataire</strong></div>

      <el-form-item label="Nom de l'entreprise">
        <el-input v-model="form.recipient.company_name" />
      </el-form-item>
      <el-form-item label="A l'attention de">
        <el-input v-model="form.recipient.attention_to" />
      </el-form-item>
      <el-form-item label="Téléphone">
        <el-input v-model="form.recipient.address.phone_number" />
      </el-form-item>
      <el-form-item label="Adresse">
        <el-input v-model="form.recipient.address.street_address" />
      </el-form-item>
      <el-form-item label="Ville">
        <el-input v-model="form.recipient.address.city" />
      </el-form-item>
      <el-form-item label="Province">
        <el-input v-model="form.recipient.address.region" />
      </el-form-item>
      <el-form-item label="Code postal">
        <el-input v-model="form.recipient.address.postal_code" />
      </el-form-item>
    </el-card>

    <el-card shadow="never" class="mb-3">
      <div slot="header"><strong>Détails du colis</strong></div>

      <el-form-item label="Valeur déclarée">
        <el-input v-model="form.additional_options.declared_value" />
      </el-form-item>

      <div
        v-for="(pkg, index) in form.packaging_properties.packages"
        :key="index"
        class="package-item"
      >
        <el-divider>Colis #{{ index + 1 }}</el-divider>

        <el-form-item label="Longueur (in)">
          <el-input v-model="pkg.length" />
        </el-form-item>
        <el-form-item label="Largeur (in)">
          <el-input v-model="pkg.width" />
        </el-form-item>
        <el-form-item label="Hauteur (in)">
          <el-input v-model="pkg.depth" />
        </el-form-item>
        <el-form-item
          :label="'Poids (' + form.packaging_properties.weight.unit + ')'"
        >
          <el-input v-model="pkg.weight" />
        </el-form-item>
        <el-form-item label="Quantité">
          <el-input v-model="pkg.quantity" />
        </el-form-item>

        <el-button
          v-if="form.packaging_properties.packages.length > 1"
          type="danger"
          @click="removePackage(index)"
        >
          Supprimer
        </el-button>
      </div>

      <el-button type="success" plain @click="addPackage"
        >+ Ajouter un colis</el-button
      >
    </el-card>

    <div class="flex justify-end mt-3">
      <el-button @click="$emit('cancel')">Annuler</el-button>
      <el-button v-if="isEditing" type="primary" @click="editEstimation"
        >Refaire l'estimation</el-button
      >
      <el-button v-else type="primary" @click="estimate"
        >Estimer l'envoi</el-button
      >
    </div>
  </el-form>
</template>

<script>
import { PACKAGING_TYPES, WEIGHT_UNITS, PAYMENT_TYPES, PAYMENT_TYPE_SENDER, DEFAULT_PACKAGING_TYPE, DEFAULT_WEIGHT_UNIT } from "../../constants/shipment";
import { Form, FormItem, Card, Button, Select, Option, Divider } from "element-ui";
 
export default {
  name: "ShipmentForm",
  components: { 
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Card.name]: Card,
    [Button.name]: Button,
    [Select.name]: Select,
    [Option.name]: Option,
    [Divider.name]: Divider,
  },
  props: {
    shippableId: { type: [String, Number], required: true },
    shippableType: { type: String, required: true },
    initialData: { type: Object, default: null },
  },
  data() {
    return {
      packagingTypes: PACKAGING_TYPES,
      weightUnits: WEIGHT_UNITS,
      paymentTypes: PAYMENT_TYPES,
      defaultWeightUnit: DEFAULT_WEIGHT_UNIT,
      form: {
        name: 'first shipment',
        sender: {
          address: {
            city: "Boucherville",
            region: "QC",
            country: "CA",
            phone_code: "+1",
            postal_code: "J4B 7L9",
            phone_number: "4504492312",
            street_address: "1440 Joliot-Curie",
          },
          residential: false,
          company_name: "ENTREPRO inc.",
          contact_name: "Véronique Lareau",
          email_address: "contact@entreproamd.com",
        },
        recipient: {
          address: {
            city: "",
            region: "",
            country: "CA",
            phone_code: "+1",
            postal_code: "",
            phone_number: "",
            street_address: "",
          },
          residential: false,
          attention_to: "",
          company_name: "",
        },
        payment_type: PAYMENT_TYPE_SENDER,
        packaging_type: DEFAULT_PACKAGING_TYPE,
        additional_options: {
          declared_value: "100",
        },
        packaging_properties: {
          weight: {
            unit: DEFAULT_WEIGHT_UNIT,
            value: "40",
          },
          packages: [
            {
              depth: "15",
              width: "20",
              length: "10",
              weight: "25",
              quantity: "1",
            },
          ],
        },
      },
    };
  },
  computed: {
    isEditing() {
      return !!this.initialData;
    },
  },
  mounted() {
    if (this.initialData) {
      this.populateForm(this.initialData);
    }
  },
  methods: {
    addPackage() {
      this.form.packaging_properties.packages.push({
        depth: "1",
        width: "1",
        length: "1",
        weight: "1",
        quantity: "1",
      });
    },
    removePackage(index) {
      this.form.packaging_properties.packages.splice(index, 1);
    },
    async estimate() {
      const payload = this.form;
      await this.$store.dispatch("elixioConnectModule/estimateShipment", {
        shippableType: this.shippableType,
        shippableId: this.shippableId,
        payload,
      });
      this.$emit("created");
    },

    async editEstimation() {
      const payload = this.form;
      await this.$store.dispatch("elixioConnectModule/editEstimation", {
        shipmentId: this.initialData.id,
        payload,
      });
      this.$emit("created");
    },

    populateForm(data) {
      this.$set(this.form, 'name', data.name || "");
      Object.assign(this.form.sender, data.sender || {});
      Object.assign(this.form.recipient, data.recipient || {});
      this.$set(this.form, 'payment_type', data.payment_type || "Sender");
      this.$set(this.form, 'packaging_type', data.packaging_type || "package");
      Object.assign(this.form.additional_options, data.additional_options || {});
      if (data.packaging_properties) {
        Object.assign(this.form.packaging_properties.weight, data.packaging_properties.weight || { unit: this.defaultWeightUnit, value: "1" });
        this.form.packaging_properties.packages = data.packaging_properties.packages || [
          {
            depth: "1",
            width: "1",
            length: "1",
            weight: "1",
            quantity: "1",
          },
        ];
      } else {
        this.form.packaging_properties = {
          weight: { unit: this.defaultWeightUnit, value: "1" },
          packages: [
            {
              depth: "1",
              width: "1",
              length: "1",
              weight: "1",
              quantity: "1",
            },
          ],
        };
      }
    },
  },
};
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1.5rem;
}
.package-item {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 1rem;
}
.flex {
  display: flex;
  justify-content: flex-end;
}
</style>
